'use client';

import useStableCallback from '@/hooks/use-stable-callback';
import { Nullable } from '@/types';
// eslint-disable-next-line no-restricted-imports
import { useSession } from 'next-auth/react';
import { useSessionStorage } from 'usehooks-ts';
import { createAuthStore } from './auth-store';
import { Session } from 'next-auth';
import {
  createContext,
  createRef,
  type ReactNode,
  useContext,
  useEffect,
  useRef
} from 'react';

export type AuthStoreApi = ReturnType<typeof createAuthStore>;
export const AuthStoreContext = createContext<AuthStoreApi | null>(null);

const authStoreRef = createRef<AuthStoreApi>();

export interface AuthStoreProviderProps {
  children: ReactNode;
  session: Session | null;
}

export function AuthStoreProvider({
  children,
  session
}: AuthStoreProviderProps) {
  const storeRef = useRef<AuthStoreApi>();
  const { update } = useSession();
  const updateSession = useStableCallback(update);

  // Initialize the ref if it hasn't been initialized yet
  if (!storeRef.current) {
    storeRef.current = createAuthStore(session, updateSession);
    // Using Object.assign to set the current property
    Object.assign(authStoreRef, { current: storeRef.current });
  }

  return (
    <AuthStoreContext.Provider value={storeRef.current}>
      {children}
    </AuthStoreContext.Provider>
  );
}

export function useAuthStore() {
  const authStoreContext = useContext(AuthStoreContext);

  if (!authStoreContext) {
    throw new Error(`useAuthStore must be used within AuthStoreProvider`);
  }

  // Using Object.assign to set the current property
  Object.assign(authStoreRef, { current: authStoreContext });

  return authStoreContext;
}

export { authStoreRef };

export type ProvisionalUser = {
  email: string;
  paymentMethodId: string;
  entitlements?: string[];
  username?: Nullable<string>;
  uuid?: Nullable<string>;
  country?: Nullable<string>;
  postalCode?: Nullable<string>;
};

const provisionalUserStorageKey = 'provisional-user-session';

export function useSessionProvisionalUser() {
  const authStore = useAuthStore();
  const isSignedIn = authStore.useTracked.isSignedIn();
  const result = useSessionStorage<ProvisionalUser | null>(
    provisionalUserStorageKey,
    null
  );

  const clear = result[2];

  useEffect(() => {
    if (isSignedIn) {
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  if (isSignedIn) {
    result[0] = null;
  }

  return result;
}
